import Map from 'ol/Map';
import { DIGISAKA_API_FIELDDELINEATION, digisakaAuthRequestInit, gptInferenceJsonHeaders } from '../../../config/digisakaGptInference';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';
import { assertFarmLevelMapZoom } from './farmLevelMapZoom';
import { deriveViewportResolutionMeters } from './mapDisplayScale';

/** Which delineation checkpoint the inference API should run. */
export type CropfieldDelineationModel = 'yolo' | 'dino';

/** Shared viewport params. ``model`` defaults to DINO when omitted. */
export type CropfieldDelineationYoloParams = {
  tile_size: number;
  overlap: number;
  resolution: number;
  conf_threshold: number;
  include_preview: boolean;
  /** DINO flip TTA; off on CPU for ~3× faster inference with negligible boundary change. */
  tta?: boolean;
  /** DINO Douglas–Peucker strength (× pixel). Higher → fewer edit vertices. Default 4. */
  smooth_epsilon?: number;
  /** DINO outer-ring vertex cap after simplification. Default 100. */
  max_polygon_vertices?: number;
  model?: CropfieldDelineationModel;
};

export type CropfieldDelineationViewportParams = CropfieldDelineationYoloParams;

/** Legacy name — YOLO viewport params. */
export type CropfieldDelineationRequestParams = CropfieldDelineationYoloParams;

/**
 * Single POST to delineate-viewport: server builds GeoTIFF from viewport PNG + EPSG:3857 extent.
 */
export async function cropfieldDelineateFromViewport(
  map: Map,
  params: CropfieldDelineationViewportParams,
  signal?: AbortSignal,
): Promise<unknown> {
  const view = map.getView();
  const size = map.getSize();
  if (!size || view.getCenter() === undefined) {
    throw new Error('Map view is not ready. Pan or zoom once and try again.');
  }

  const zoomCheck = assertFarmLevelMapZoom(view.getZoom());
  if (!zoomCheck.ok) {
    throw new Error(zoomCheck.message);
  }

  // Listen *before* renderSync: if renderSync runs first, rendercomplete may already have fired
  // and the handler would never run — leaving this Promise pending (no API call) until some
  // other interaction (e.g. tree detection) forces another render.
  await new Promise<void>((resolve) => {
    map.once('rendercomplete', () => resolve());
    map.renderSync();
  });

  const canvas = map.getViewport().querySelector('canvas') as HTMLCanvasElement | null;
  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error('Could not read map canvas.');
  }

  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL('image/png');
  } catch {
    throw new Error(
      'Canvas is tainted (likely due to tile CORS). Try the OpenStreetMap base layer or another source that allows export.'
    );
  }

  const extent3857 = view.calculateExtent(size) as [number, number, number, number];
  if (signal?.aborted) {
    throw new DOMException('Analysis cancelled', 'AbortError');
  }

  const resolution = deriveViewportResolutionMeters(map, params.resolution);

  const body = {
    png_base64: dataUrl,
    width: canvas.width,
    height: canvas.height,
    extent_3857: extent3857,
    tile_size: params.tile_size,
    overlap: params.overlap,
    resolution,
    conf_threshold: params.conf_threshold,
    include_preview: params.include_preview,
    tta: params.tta ?? false,
    smooth_epsilon: params.smooth_epsilon ?? 4,
    max_polygon_vertices: params.max_polygon_vertices ?? 100,
    model: params.model === 'yolo' ? 'yolo' : 'dino',
  };

  const res = await fetch(
    DIGISAKA_API_FIELDDELINEATION,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: gptInferenceJsonHeaders(),
      signal,
      body: JSON.stringify(body),
    })
  );

  notifyInferenceComputeUsage(res);

  if (!res.ok) {
    let msg = `API error: ${res.status}`;
    try {
      const errBody = await res.json();
      if (errBody?.detail) {
        msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
      }
    } catch {
      const t = await res.text();
      if (t) msg = t;
    }
    throw new Error(msg);
  }

  return res.json();
}
